import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import BrandLogo from '@/components/BrandLogo';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '../utils/toast';
import { Loader2, ArrowRight, Menu, X, Monitor, LogIn, CheckCircle, MailCheck, MapPin } from 'lucide-react';
import Footer from '@/components/Footer';
import axios from 'axios';
import { sortStringOptions, sortObjectOptions, INSTITUTION_TYPES } from '@/utils/sortOptions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const INITIAL_SIGNUP_FORM_DATA = {
  full_name: '',
  phone_number: '',
  email: '',
  organization_name: '',
  type_of_organization: 'Healthcare Network',
  department_id: '',
  job_function_id: '',
  location: '',
  terms_accepted: false
};

const SignUpPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // OTP verification state
  const [showOTP, setShowOTP] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registrationComplete, setRegistrationComplete] = useState(false);
  
  const [formData, setFormData] = useState(INITIAL_SIGNUP_FORM_DATA);
  const [departments, setDepartments] = useState([]);
  const [jobFunctions, setJobFunctions] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  
  // Location autocomplete state
  const [locationSearch, setLocationSearch] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Organization types from centralized source
  const organizationTypes = INSTITUTION_TYPES;

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newData = { ...formData, [name]: value };
    
    // Auto-populate organization_name with full_name for Individual type
    if (name === 'full_name' && formData.type_of_organization === 'Individual') {
      newData.organization_name = value;
    }
    
    setFormData(newData);
  };

  const handleSelectChange = async (name, value) => {
    try {
      if (name === 'department_id') {
        // When department changes, clear job role and fetch new job roles for that department
        setFormData(prev => ({ ...prev, [name]: value, job_function_id: '' }));
        setJobFunctions([]); // Clear existing job roles
        
        // Fetch job roles for the selected department
        if (value && formData.type_of_organization === 'Individual') {
          setLoadingDepartments(true);
          try {
            const jobResponse = await axios.get(`${API}/public/default-job-functions`, {
              params: { department_id: value }
            });
            setJobFunctions(jobResponse.data.job_functions || []);
          } catch (error) {
            console.error('Failed to fetch job roles:', error);
            toast.error('Failed to load job roles for selected department.');
          } finally {
            setLoadingDepartments(false);
          }
        }
      } else if (name === 'job_function_id') {
        // Simple update for job function selection
        setFormData(prev => ({ ...prev, [name]: value }));
      } else {
        // For other fields like type_of_organization
        setFormData(prev => {
          const newData = { ...prev, [name]: value };
          // Reset department and job role when organization type changes
          if (name === 'type_of_organization') {
            newData.department_id = '';
            newData.job_function_id = '';
            // Auto-populate organization_name with full_name for Individual
            if (value === 'Individual' && prev.full_name) {
              newData.organization_name = prev.full_name;
            }
          }
          return newData;
        });
        // Reset job functions when organization type changes
        if (name === 'type_of_organization') {
          setJobFunctions([]);
        }
      }
    } catch (error) {
      console.error('Error in handleSelectChange:', error);
      toast.error('An error occurred. Please try again.');
    }
  };

  // Fetch default departments when Individual is selected
  useEffect(() => {
    const fetchDepartments = async () => {
      if (formData.type_of_organization === 'Individual') {
        setLoadingDepartments(true);
        try {
          const deptResponse = await axios.get(`${API}/public/default-departments`);
          setDepartments(deptResponse.data.departments || []);
          setJobFunctions([]); // Clear job roles until department is selected
        } catch (error) {
          console.error('Failed to fetch departments:', error);
          toast.error('Failed to load departments. Please refresh the page.');
        } finally {
          setLoadingDepartments(false);
        }
      } else {
        // Clear when not Individual
        setDepartments([]);
        setJobFunctions([]);
      }
    };
    
    fetchDepartments();
  }, [formData.type_of_organization]);

  // Location search with debounce
  useEffect(() => {
    if (!locationSearch || locationSearch.length < 2) {
      setLocationSuggestions([]);
      setShowLocationDropdown(false);
      return;
    }
    
    const timer = setTimeout(async () => {
      setLoadingLocations(true);
      try {
        const response = await axios.get(`${API}/public/locations`, {
          params: { search: locationSearch, limit: 10 }
        });
        setLocationSuggestions(response.data.locations || []);
        setShowLocationDropdown(true);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      } finally {
        setLoadingLocations(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [locationSearch]);

  const handleLocationSelect = (location) => {
    const displayValue = location.state ? `${location.city}, ${location.state}` : location.city;
    setFormData(prev => ({ ...prev, location: displayValue }));
    setLocationSearch(displayValue);
    setShowLocationDropdown(false);
    setLocationSuggestions([]);
  };

  const handleCheckboxChange = (checked) => {
    setFormData({ ...formData, terms_accepted: checked });
  };

  // Get dynamic label for organization name based on selected type
  const getOrganizationNameLabel = () => {
    if (!formData.type_of_organization) {
      return 'Organization name';
    }
    return `Name of the ${formData.type_of_organization}`;
  };

  // Get dynamic placeholder for organization name based on selected type
  const getOrganizationNamePlaceholder = () => {
    if (!formData.type_of_organization) {
      return 'Enter the name of your organization here';
    }
    return `Enter the name of your ${formData.type_of_organization.toLowerCase()} here`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.full_name) {
      toast.error('Please enter your full name.');
      return;
    }
    if (!formData.phone_number) {
      toast.error('Please enter your phone number.');
      return;
    }
    if (!formData.email) {
      toast.error('Please enter your email address.');
      return;
    }
    if (!formData.organization_name) {
      toast.error('Please enter your organization name.');
      return;
    }
    if (!formData.type_of_organization) {
      toast.error('Please select your organization type.');
      return;
    }
    // Validate Department and Job Role for Individual
    if (formData.type_of_organization === 'Individual') {
      if (!formData.department_id) {
        toast.error('Please select a Department.');
        return;
      }
      if (!formData.job_function_id) {
        toast.error('Please select a Job Role.');
        return;
      }
    }
    if (!formData.terms_accepted) {
      toast.error('Please accept the Terms & conditions and Privacy policy.');
      return;
    }

    setLoading(true);
    try {
      // Prepare request payload
      const signupPayload = {
        full_name: formData.full_name,
        phone_number: formData.phone_number,
        email: formData.email,
        institution_name: formData.organization_name,
        institution_type: formData.type_of_organization,
        terms_accepted: formData.terms_accepted,
        location: formData.location || null
      };
      
      // Add department and job role for Individual
      if (formData.type_of_organization === 'Individual') {
        signupPayload.department_id = formData.department_id;
        signupPayload.job_function_id = formData.job_function_id;
      }
      
      const response = await axios.post(`${API}/auth/signup/send-otp`, signupPayload);
      
      setRegisteredEmail(formData.email);
      setShowOTP(true);
      toast.success('OTP sent to your email address.');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerifyOTP = async () => {
    if (!otpValue || otpValue.length !== 6) {
      toast.error('Please enter the 6-digit OTP.');
      return;
    }
    
    setVerifying(true);
    try {
      const response = await axios.post(`${API}/auth/signup/verify-otp`, {
        email: registeredEmail,
        otp: otpValue
      });
      
      setRegistrationComplete(true);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'OTP verification failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setVerifying(false);
    }
  };
  
  const handleResendOTP = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/signup/resend-otp`, { email: registeredEmail });
      toast.success('A new OTP has been sent to your email.');
      setOtpValue('');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to resend OTP. Please try again.';
      toast.error(errorMessage);
    } finally {
      setResending(false);
    }
  };

  const isIndividualSignup = formData.type_of_organization === 'Individual';

  const handleSuccessAction = () => {
    if (isIndividualSignup) {
      navigate('/signin');
      return;
    }

    setRegistrationComplete(false);
    setShowOTP(false);
    setOtpValue('');
    setRegisteredEmail('');
    setLocationSearch('');
    setLocationSuggestions([]);
    setShowLocationDropdown(false);
    setDepartments([]);
    setJobFunctions([]);
    setFormData(INITIAL_SIGNUP_FORM_DATA);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header/Navigation */}
      <header className="bg-white border-b border-gray-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <BrandLogo />
            </div>

            {/* Desktop Navigation & Buttons - All aligned right with equal spacing */}
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => navigate('/')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Home
              </button>
              <button onClick={() => navigate('/solutions')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                The solution
              </button>
              <button onClick={() => navigate('/pricing')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Pricing
              </button>
              <button onClick={() => navigate('/contact')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Contact us
              </button>
              <Button 
                onClick={() => navigate('/request-demo')}
                className="bg-[#10B981] hover:bg-[#059669] text-white text-sm px-4 py-2 rounded-lg transition-colors"
              >
                <Monitor className="w-4 h-4 mr-2" />
                Request for demo
              </Button>
              <Button 
                onClick={() => navigate('/signin')}
                className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white text-sm px-4 py-2 rounded-lg transition-colors"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign in
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3 border-t border-gray-200 pt-4">
              <button 
                onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Home
              </button>
              <button 
                onClick={() => { navigate('/solutions'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                The solution
              </button>
              <button 
                onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={() => { navigate('/contact'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Contact us
              </button>
              <div className="space-y-2 pt-2">
                <Button 
                  onClick={() => { navigate('/request-demo'); setMobileMenuOpen(false); }}
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white"
                >
                  Request for demo
                </Button>
                <Button 
                  onClick={() => { navigate('/signin'); setMobileMenuOpen(false); }}
                  className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
                >
                  Sign in
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content - Two Column Layout */}
      <main className="flex-1 grid md:grid-cols-2">
        {/* Left Side - Futuristic Healthcare/Security Image */}
        <div className="hidden md:block relative bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900">
          <img
            src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1000&h=1000&fit=crop"
            alt="Digital Healthcare Security"
            className="w-full h-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 to-purple-900/60"></div>
          
          {/* Digital Overlays */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="grid grid-cols-2 gap-8">
              {/* Lock Shield Icon */}
              <div className="w-32 h-32 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
                <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              
              {/* Brain/AI Icon */}
              <div className="w-32 h-32 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
                <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              
              {/* Health/Heart Monitor Icon */}
              <div className="w-32 h-32 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
                <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              
              {/* Medical Cross Icon */}
              <div className="w-32 h-32 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
                <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Sign Up Form / OTP / Success */}
        <div className="flex items-center justify-center p-6 md:p-12 bg-white">
          <div className="w-full max-w-2xl">
            
            {/* Registration Complete Success Screen */}
            {registrationComplete ? (
              <div className="text-center py-12">
                <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                {isIndividualSignup ? (
                  <>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Registration Successful!</h2>
                    <p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
                      You have been successfully registered. We have sent the details of your registration to your email address. Please review the email and use the provided details to access your account accordingly.
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Signup Request Submitted</h2>
                    <p className="text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                      Thank you for registering with Eptral.Hospital. Your signup request has been submitted successfully.
                      <br /><br />
                      Our team will review your request, and a sales representative may connect with you to discuss the next steps.
                      <br /><br />
                      Once your account is approved and activated by the admin, you will receive a welcome email with your login credentials to access the portal.
                    </p>
                  </>
                )}
                <Button
                  onClick={handleSuccessAction}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
                >
                  {isIndividualSignup ? (
                    <>
                      <LogIn className="w-4 h-4 mr-2" />
                      Sign in to your account
                    </>
                  ) : (
                    'OK, Understood'
                  )}
                </Button>
              </div>
            ) : showOTP ? (
              /* OTP Verification Screen */
              <div className="max-w-md mx-auto">
                <div className="text-center mb-8">
                  <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <MailCheck className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h2>
                  <p className="text-gray-600 leading-relaxed">
                    Enter the OTP sent to your email ID to verify your email address for future communication.
                  </p>
                  <p className="text-sm text-blue-600 font-medium mt-2">{registeredEmail}</p>
                </div>
                
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="otp">Enter OTP</Label>
                    <Input
                      id="otp"
                      type="text"
                      maxLength={6}
                      value={otpValue}
                      onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      className="w-full text-center text-2xl tracking-[0.5em] font-mono py-4"
                      autoFocus
                    />
                    <p className="text-xs text-gray-500 text-center">OTP expires in 10 minutes</p>
                  </div>
                  
                  <Button
                    onClick={handleVerifyOTP}
                    disabled={verifying || otpValue.length !== 6}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-base font-medium"
                  >
                    {verifying ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify OTP'
                    )}
                  </Button>
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Didn't receive the OTP?</p>
                    <button
                      onClick={handleResendOTP}
                      disabled={resending}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:text-gray-400"
                    >
                      {resending ? 'Sending...' : 'Resend OTP'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
            /* Registration Form */
            <div>
            {/* Already have account link */}
            <div className="mb-6 text-right">
              <span className="text-sm text-gray-600">Already have an account? </span>
              <Link to="/signin" className="text-sm text-blue-600 hover:underline font-medium">
                Sign in
              </Link>
            </div>

            {/* Form Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Sign up
              </h1>
              <p className="text-gray-600">
                Please fill out the form below to register with Eptral.Hospital and access its features.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="full_name">
                  Full name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  placeholder="Enter your full name here"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full"
                />
              </div>

              {/* Email and Phone Number */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email address here"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone_number">
                    Phone number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone_number"
                    name="phone_number"
                    type="tel"
                    placeholder="Enter your phone number here"
                    value={formData.phone_number}
                    onChange={handleChange}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Row: Signing up as and Organization Name */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type_of_organization">
                    Signing up as <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.type_of_organization}
                    onValueChange={(value) => handleSelectChange('type_of_organization', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select type of organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizationTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organization_name">
                    {getOrganizationNameLabel()} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="organization_name"
                    name="organization_name"
                    type="text"
                    placeholder={getOrganizationNamePlaceholder()}
                    value={formData.organization_name}
                    onChange={handleChange}
                    className="w-full"
                    readOnly={formData.type_of_organization === 'Individual'}
                    disabled={formData.type_of_organization === 'Individual'}
                  />
                </div>
              </div>

              {/* Department and Job Role - Only show for Individual */}
              {formData.type_of_organization === 'Individual' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="department_id">
                      Department <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.department_id}
                      onValueChange={(value) => handleSelectChange('department_id', value)}
                      disabled={loadingDepartments}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={loadingDepartments ? "Loading..." : "Select Department"} />
                      </SelectTrigger>
                      <SelectContent>
                        {sortObjectOptions(departments, 'department_name').map((dept) => (
                          <SelectItem key={dept.department_id} value={dept.department_id}>
                            {dept.department_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="job_function_id">
                      Job Role <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.job_function_id}
                      onValueChange={(value) => handleSelectChange('job_function_id', value)}
                      disabled={loadingDepartments || !formData.department_id}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={
                          !formData.department_id 
                            ? "Select Department first" 
                            : loadingDepartments 
                              ? "Loading..." 
                              : "Select Job Role"
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        {jobFunctions.length === 0 && formData.department_id ? (
                          <div className="px-2 py-1.5 text-sm text-gray-500 text-center">
                            No job roles found for this department
                          </div>
                        ) : (
                          sortObjectOptions(jobFunctions, 'job_function_name').map((job) => (
                            <SelectItem key={job.job_function_id} value={job.job_function_id}>
                              {job.job_function_name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Location Field */}
              <div className="space-y-2">
                <Label htmlFor="location">
                  Location
                </Label>
                <div className="relative">
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="location"
                      name="location"
                      type="text"
                      placeholder="Search for your city..."
                      value={locationSearch}
                      onChange={(e) => {
                        setLocationSearch(e.target.value);
                        if (!e.target.value) {
                          setFormData(prev => ({ ...prev, location: '' }));
                        }
                      }}
                      onFocus={() => {
                        if (locationSuggestions.length > 0) setShowLocationDropdown(true);
                      }}
                      onBlur={() => {
                        // Delay to allow click on dropdown
                        setTimeout(() => setShowLocationDropdown(false), 200);
                      }}
                      className="w-full pl-9"
                      autoComplete="off"
                    />
                    {loadingLocations && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                    )}
                  </div>
                  {showLocationDropdown && locationSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {locationSuggestions.map((loc) => (
                        <button
                          key={loc.location_id}
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleLocationSelect(loc)}
                        >
                          <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          <span>{loc.city}{loc.state ? `, ${loc.state}` : ''}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms_accepted"
                  checked={formData.terms_accepted}
                  onCheckedChange={handleCheckboxChange}
                  className="mt-1"
                />
                <label htmlFor="terms_accepted" className="text-sm text-gray-700 leading-tight">
                  I accept the{' '}
                  <Link to="/terms-and-conditions" className="text-blue-600 hover:underline">
                    Terms & conditions
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy-policy" className="text-blue-600 hover:underline">
                    Privacy policy
                  </Link>{' '}
                  <span className="text-red-500">*</span>
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-base font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Signing up...
                  </>
                ) : (
                  <>
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Sign up
                  </>
                )}
              </Button>
            </form>
          </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SignUpPage;
